import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Key, LayoutDashboard, Bell, Users, Calendar, Settings, HelpCircle, Ticket, FileText } from "lucide-react";
import FATickets from "./FATickets";
import { useAuth, API } from "../../context/AuthContext";
import { useTenant } from "../../context/TenantContext";
import { ChangePassword } from "../../components/shared/ChangePassword";
import AdminAnalytics from "./FAAnalytics";
import ManageGuests from "../admin/ManageGuests";
import ManageAccountants from "../admin/ManageAccountants";
import ManageMerchants from "../admin/ManageMerchants";
import ManageShopAdmins from "../admin/ManageShopAdmins";
import ManageEvents from "../admin/ManageEvents";
import ManageMembers from "./ManageMembers";
import ManageMemberTypes from "./ManageMemberTypes";
import PendingApprovals from "./PendingApprovals";
import PeopleSection from "./PeopleSection";
import TasksSection from "./TasksSection";
import EventPnL from "./EventPnL";
import ManageCategories from "./ManageCategories";
import SepaMandatesBrowser from "./SepaMandatesBrowser";
import DigitalChargesSettings from "./DigitalChargesSettings";
import PosChargesSettings from "./PosChargesSettings";
import AuditLogs from "./AuditLogs";

export default function FinanceAdminDashboard() {
  const [tab, setTab] = useState("dashboard");
  const [configTab, setConfigTab] = useState("types");
  const [pendingCount, setPendingCount] = useState(0);
  const [pnlEventId, setPnlEventId] = useState(null);
  const { logout, user, authAxios } = useAuth();
  const { hasModule } = useTenant();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== "finance_admin") navigate("/" + (window.location.pathname.split("/")[1] || "mymaitree") + "/admin");
  }, [user]);

  useEffect(() => {
    if (!hasModule("memberships") && !hasModule("guest_portal")) return;
    const fetchPending = async () => {
      try { const res = await authAxios.get("/members/pending-approvals"); setPendingCount(res.data.length); }
      catch (err) { console.error("Request failed:", err?.message || err); setPendingCount(0); }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    // iter139: land on the tenant admin login, not the root splash.
    const slug = window.location.pathname.split("/")[1] || "mymaitree";
    try { await axios.post(`${API}/finance-admin/logout`, {}, { withCredentials: true }); } catch (err) { console.error("Request failed:", err?.message || err); }
    logout(); navigate(`/${slug}/admin`);
  };

  // Build tabs based on enabled modules
  const tabs = [
    { id: "dashboard", icon: LayoutDashboard, label: "Dashboard" },
    ...(hasModule("memberships") || hasModule("guest_portal") ? [{ id: "tasks", icon: Bell, label: "Tasks", badge: pendingCount }] : []),
    ...(hasModule("memberships") || hasModule("guest_portal") ? [{ id: "members", icon: Users, label: "People" }] : []),
    ...(hasModule("events") ? [{ id: "events", icon: Calendar, label: "Events" }] : []),
    ...(hasModule("ticketing") ? [{ id: "tickets", icon: Ticket, label: "Tickets" }] : []),
    { id: "audit", icon: FileText, label: "Audit" },
    { id: "config", icon: Settings, label: "Settings" },
  ];

  const configTabs = [
    ...(hasModule("memberships") ? [{ id: "types", label: "Member Types" }] : []),
    ...(hasModule("memberships") ? [{ id: "sepa", label: "SEPA Mandates" }] : []),
    ...(hasModule("events") ? [{ id: "categories", label: "Categories" }] : []),
    ...(hasModule("event_commerce") ? [{ id: "accountants", label: "Accountants" }] : []),
    ...(hasModule("event_commerce") ? [{ id: "merchants", label: "Merchants" }] : []),
    ...(hasModule("ticketing") ? [{ id: "shop_admins", label: "Shop Admins" }] : []),
    ...(hasModule("event_commerce") ? [{ id: "charges", label: "Payment Charges" }] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="finance-admin-dashboard">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/mymaitree-logo.png" alt="My Maitree" className="h-8 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
            <div>
              <p className="text-slate-400 text-xs">Finance Admin</p>
              <h1 className="text-sm font-bold text-[#1e3a5f]">{user?.name || "Finance Admin"}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setTab("changePassword")} className={`p-2 rounded-full hover:bg-slate-100 ${tab === "changePassword" ? "text-[#8b5cf6]" : "text-slate-400"}`} data-testid="header-settings-btn"><Key className="w-5 h-5" /></button>
            <button onClick={() => navigate("/help?role=finance_admin")} className="p-2 rounded-full hover:bg-slate-100 text-slate-400" data-testid="help-btn"><HelpCircle className="w-5 h-5" /></button>
            <button onClick={handleLogout} className="p-2 rounded-full hover:bg-red-50 text-red-500" data-testid="logout-btn"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
        {tab !== "changePassword" && (
          <div className="max-w-5xl mx-auto flex border-t">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 py-3 flex items-center justify-center gap-1.5 text-sm font-medium border-b-2 relative ${tab === t.id ? "border-[#1e3a5f] text-[#1e3a5f]" : "border-transparent text-slate-400 hover:text-slate-600"}`} data-testid={`nav-${t.id}`}>
                <t.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{t.label}</span>
                {t.badge > 0 && <span className="absolute -top-0.5 right-2 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{t.badge}</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-5xl mx-auto p-6">
        {tab === "dashboard" && <DashboardOverview pendingCount={pendingCount} onGoApprovals={() => setTab("tasks")} authAxios={authAxios} />}
        {tab === "tasks" && <TasksSection onCountChange={setPendingCount} />}
        {tab === "members" && <PeopleSection />}
        {tab === "events" && (pnlEventId ? <EventPnL eventId={pnlEventId} onBack={() => setPnlEventId(null)} /> : <ManageEvents onViewPnL={(id) => setPnlEventId(id)} />)}
        {tab === "tickets" && <FATickets />}
        {tab === "audit" && <AuditLogs />}
        {tab === "config" && (
          <div>
            <div className="flex gap-2 mb-4 overflow-x-auto">
              {configTabs.map(ct => (
                <button key={ct.id} onClick={() => setConfigTab(ct.id)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${configTab === ct.id ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600 border"}`} data-testid={`config-${ct.id}`}>
                  {ct.label}
                </button>
              ))}
            </div>
            {configTab === "types" && <ManageMemberTypes />}
            {configTab === "sepa" && <SepaMandatesBrowser />}
            {configTab === "categories" && <ManageCategories />}
            {configTab === "accountants" && <ManageAccountants />}
            {configTab === "merchants" && <ManageMerchants />}
            {configTab === "shop_admins" && <ManageShopAdmins />}
            {configTab === "charges" && <><DigitalChargesSettings /><PosChargesSettings /></>}
          </div>
        )}
        {tab === "changePassword" && <ChangePassword onBack={() => setTab("dashboard")} role="finance-admin" />}
      </div>
    </div>
  );
}

function DashboardOverview({ pendingCount, onGoApprovals, authAxios }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [ppl, evt, fin] = await Promise.all([
          authAxios.get("/fa-analytics/people"),
          authAxios.get("/fa-analytics/events"),
          authAxios.get("/fa-analytics/finance"),
        ]);
        setData({ people: ppl.data, events: evt.data, finance: fin.data });
      } catch (err) { console.error("Request failed:", err?.message || err); }
    };
    fetchAll();
  }, []);

  if (!data) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  return (
    <div data-testid="dashboard-overview">
      <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Overview</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {/* People card */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 text-center border-b">
            <p className="text-3xl font-extrabold text-[#1e3a5f]">{data.people.active_members}</p>
            <p className="text-slate-500 text-xs">Active Members</p>
          </div>
          <div className="p-3 bg-slate-50 text-center">
            <p className="text-lg font-bold text-green-600">{data.people.engaged_pct}%</p>
            <p className="text-slate-400 text-[10px]">Engagement (Last Quarter)</p>
          </div>
        </div>

        {/* Events card — with OVERALL profitability (all revenue vs all cost, excluding unused wallet) */}
        {(() => {
          const rev = data.finance?.total_revenue || 0;
          const cost = data.finance?.total_costs || 0;
          const overall = cost > 0 ? Math.round((rev / cost * 100 - 100) * 10) / 10 : (rev > 0 ? 100 : 0);
          const positive = overall >= 0;
          return (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="p-4 text-center border-b">
                <p className="text-3xl font-extrabold text-[#1e3a5f]">{data.events.events_ytd}</p>
                <p className="text-slate-500 text-xs">Events YTD</p>
              </div>
              <div className="p-3 bg-slate-50 text-center">
                <p className={`text-lg font-bold ${positive ? "text-green-600" : "text-red-500"}`} data-testid="overview-profitability">{overall > 0 ? "+" : ""}{overall}%</p>
                <p className="text-slate-400 text-[10px]">Profitability YTD (overall)</p>
              </div>
            </div>
          );
        })()}

        {/* Finance card */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 text-center border-b">
            <p className="text-3xl font-extrabold text-green-600" data-testid="overview-total-revenue">{"\u20ac"}{data.finance.total_revenue.toFixed(2)}</p>
            <p className="text-slate-500 text-xs">Total Revenue</p>
          </div>
          <div className="grid grid-cols-2 divide-x bg-slate-50">
            <div className="p-2 text-center">
              <p className="text-sm font-bold text-red-500" data-testid="overview-total-cost">{"\u20ac"}{data.finance.total_costs.toFixed(2)}</p>
              <p className="text-slate-400 text-[10px]">Total Cost</p>
            </div>
            <div className="p-2 text-center">
              <p className="text-sm font-bold text-amber-600" data-testid="overview-unused-wallet">{"\u20ac"}{data.finance.unused_wallet.toFixed(2)}</p>
              <p className="text-slate-400 text-[10px]">Unused Wallet</p>
            </div>
          </div>
        </div>
      </div>

      {pendingCount > 0 && (
        <button onClick={onGoApprovals} className="w-full bg-amber-50 border border-amber-200 rounded-2xl p-3 mb-4 text-center hover:shadow-md transition-shadow">
          <span className="text-amber-600 font-bold">{pendingCount} Pending Approval{pendingCount > 1 ? "s" : ""}</span>
        </button>
      )}

      <AdminAnalytics />
    </div>
  );
}
